// ABOUTME: Retry, cancel and delete of a job through the REST API, each reported with a snackbar
// ABOUTME: carrying the server's error message; calls back so the caller can refresh its data.

import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cancelJob, deleteJob, jobApiErrorMessage, retryJob } from '../../api/jobApi';
import { useJobTypeLabel } from '../../registry';
import { Jobs } from '../../types';

const messages = defineMessages({
    retried: { id: 'job.action.retry.success', defaultMessage: '{job}: new attempt started' },
    retryFailed: {
        id: 'job.action.retry.error',
        defaultMessage: '{job} could not be retried: {detail}',
    },
    cancelled: { id: 'job.action.cancel.success', defaultMessage: '{job}: cancellation requested' },
    cancelFailed: {
        id: 'job.action.cancel.error',
        defaultMessage: '{job} could not be cancelled: {detail}',
    },
    deleted: { id: 'job.action.delete.success', defaultMessage: '{job} deleted' },
    deleteFailed: {
        id: 'job.action.delete.error',
        defaultMessage: '{job} could not be deleted: {detail}',
    },
});

type JobRef = Pick<Jobs, 'id' | 'type'>;

export type JobAction = 'retry' | 'cancel' | 'delete';

/** `onChanged` learns which action succeeded: after a delete there is no job left to reload. */
export function useJobActions(onChanged?: (action: JobAction) => void) {
    const intl = useIntl();
    const jobTypeLabel = useJobTypeLabel();
    const [pendingId, setPendingId] = useState<string | null>(null);

    const run = async (
        action: JobAction,
        job: JobRef,
        call: (id: string) => Promise<unknown>,
        success: (typeof messages)[keyof typeof messages],
        failure: (typeof messages)[keyof typeof messages],
    ) => {
        const label = jobTypeLabel(job.type);
        setPendingId(job.id);
        try {
            await call(job.id);
            SnackBarUtils.success(intl.formatMessage(success, { job: label }));
            onChanged?.(action);
            return true;
        } catch (e) {
            SnackBarUtils.error(
                intl.formatMessage(failure, { job: label, detail: jobApiErrorMessage(e) }),
            );
            return false;
        } finally {
            setPendingId(null);
        }
    };

    return {
        pendingId,
        retry: (job: JobRef) => run('retry', job, retryJob, messages.retried, messages.retryFailed),
        cancel: (job: JobRef) =>
            run('cancel', job, cancelJob, messages.cancelled, messages.cancelFailed),
        remove: (job: JobRef) =>
            run('delete', job, deleteJob, messages.deleted, messages.deleteFailed),
    };
}
